import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthUser } from '../services/auth';
import { useInstitution } from '../contexts/InstitutionContext';
import { preserveInstitutionParams } from '../utils/navigation';
import './Sidebar.css';
import StorageUsage from './StorageUsage';
import { UserProfilePopup } from './UserProfilePopup';

interface SidebarProps {
  currentUser: AuthUser | null;
}

const Sidebar: React.FC<SidebarProps> = ({ currentUser }) => {
  const location = useLocation();
  const { t } = useTranslation('common');
  const { institution, theme, applyTheme, institutionName } = useInstitution();
  const [showProfilePopup, setShowProfilePopup] = React.useState(false);
  const [localUser, setLocalUser] = React.useState<AuthUser | null>(currentUser);
  
  const isActive = (path: string) => location.pathname === path;
  
  // Apply institution theming
  React.useEffect(() => {
    applyTheme();
  }, [theme, applyTheme]);

  // Update localUser when currentUser changes
  React.useEffect(() => {
    setLocalUser(currentUser);
  }, [currentUser]);
  
  // Get the base path from CSS variable
  const basePath = getComputedStyle(document.documentElement).getPropertyValue('--base-path').trim();
  
  React.useEffect(() => {
    const infoIcons = document.querySelectorAll('.info-icon');
    infoIcons.forEach(icon => {
      (icon as HTMLElement).style.backgroundImage = `url('${basePath}/info.svg')`;
    });
  }, [basePath]);

  const handleSettingsClick = () => {
    setShowProfilePopup(true);
  };

  const handleProfileUpdate = (updatedUser: AuthUser) => {
    setLocalUser(updatedUser);
  };

  return (
    <>
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            {institution && institution.logo_url ? (
              <img 
                src={institution.logo_url}
                alt={`${institutionName} Logo`} 
                className="institution-logo-sidebar" 
                style={{ pointerEvents: 'none' }}
              />
            ) : (
              <img 
                src={`${basePath}/maxel_logo.svg`}
                alt="Maxel Logo" 
                className="logo-image" 
                style={{ pointerEvents: 'none' }}
              />
            )}
{/* Beta icon hidden per user request
            <img 
              src="/beta_icon.svg"
              alt="Beta" 
              className="beta-icon" 
              style={{ pointerEvents: 'none' }}
            />
            */}
          </div>
          {currentUser && (
            <button onClick={handleSettingsClick} className="header-settings-link">
              <img src="/settings.svg" alt={t('navigation.settings')} width={18} height={18} />
            </button>
          )}
        </div>
        
        <nav className="sidebar-nav">
          <Link to={preserveInstitutionParams("/")} className={`sidebar-nav-item ${isActive('/') ? 'active' : ''}`}>
            <span className="sidebar-icon"><img src="/1.svg" alt="Home" width={20} height={20} /></span>
            <span className="text">{t('navigation.uploadProcess')}</span>
          </Link>
          
          <Link to={preserveInstitutionParams("/projects")} className={`sidebar-nav-item ${isActive('/projects') ? 'active' : ''}`}>
            <span className="sidebar-icon"><img src="/2.svg" alt="Previous Projects" width={20} height={20} /></span>
            <span className="text">{t('navigation.previousProjects')}</span>
          </Link>
          
          <Link to={preserveInstitutionParams("/ai-support")} className={`sidebar-nav-item ${isActive('/ai-support') ? 'active' : ''}`}>
            <span className="sidebar-icon"><img src="/3.svg" alt="AI Support" width={20} height={20} /></span>
            <span className="text">{t('navigation.aiSupport')}</span>
          </Link>
          
          <div className="sidebar-nav-item coming-soon" data-tooltip={t('messages.comingSoon')}> 
            <span className="sidebar-icon"><img src="/4.svg" alt="Automatic Quotation" width={20} height={20} /></span>
            <span className="text">{t('navigation.automaticQuotation')}</span>
            <span className="info-icon"></span>
          </div>
          
          <div className="sidebar-nav-item coming-soon" data-tooltip={t('messages.comingSoon')}> 
            <span className="sidebar-icon"><img src="/5.svg" alt="G-Code Queue" width={20} height={20} /></span>
            <span className="text">{t('navigation.gcodeQueue')}</span>
            <span className="info-icon"></span>
          </div>
          
          <div className="sidebar-nav-item coming-soon" data-tooltip={t('messages.comingSoon')}> 
            <span className="sidebar-icon"><img src="/6.svg" alt="3D Print History" width={20} height={20} /></span>
            <span className="text">{t('navigation.printHistory')}</span>
            <span className="info-icon"></span>
          </div>
          
          <div className="sidebar-nav-item coming-soon" data-tooltip={t('messages.comingSoon')}> 
            <span className="sidebar-icon"><img src="/7.svg" alt="Statistics" width={20} height={20} /></span>
            <span className="text">{t('navigation.statistics')}</span>
            <span className="info-icon"></span>
          </div>
          
          <div className="sidebar-nav-item coming-soon" data-tooltip={t('messages.comingSoon')}> 
            <span className="sidebar-icon"><img src="/8.svg" alt="Prints and Materials" width={20} height={20} /></span>
            <span className="text">{t('navigation.printsAndMaterials')}</span>
            <span className="info-icon"></span>
          </div>
        </nav>

        <div className="sidebar-footer">
          {/* Show storage usage only for authenticated users */}
          {currentUser ? (
            <StorageUsage />
          ) : (
            <Link to={preserveInstitutionParams("/login")} className="login-button-sidebar" style={{ padding: '10px 0' }}>
              <span className="login-button-text">{t('buttons.loginSignup')}</span>
            </Link>
          )}
        </div>
      </div>
      
      {/* Profile Popup */}
      {showProfilePopup && localUser && (
        <UserProfilePopup 
          user={localUser} 
          onClose={() => setShowProfilePopup(false)}
          onProfileUpdate={handleProfileUpdate}
        />
      )}
    </>
  );
};

export default Sidebar; 